import { authenticatedResponseSchema, type AuthenticatedResponse } from '@growup/accounts-shared';
import type { z } from 'zod';

import { friendlyMessage } from './messages';

/**
 * Talks to the account APIs behind the same origin: `/api/identity/` (account,
 * sign-in, second factor) and `/api/members/` (level, profile, social, guardian).
 *
 * The access token (15 min) lives only in memory. The refresh token is an
 * httpOnly cookie that identity-api sets for this host; a reload or an expired
 * token asks for a new one with it (docs/contas/README.md).
 */

/** identity-api wants it on refresh and logout, so a plain cross-site form cannot use the cookie. */
const CLIENT_HEADER = { 'x-growup-client': 'streaming-web' };

let accessToken: string | null = null;
const listeners = new Set<(token: string | null) => void>();

/** A failed call. `fields` maps a request path (`address.postalCode`) to its problem. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

export function getToken(): string | null {
  return accessToken;
}

export function setToken(token: string | null): void {
  accessToken = token;
  for (const listener of listeners) listener(token);
}

/** Called whenever the token changes (sign-in, refresh, expiry, sign-out). */
export function onTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let refreshing: Promise<boolean> | null = null;

/** Gets a new access token from the refresh cookie. `false` means signed out. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const response = await fetch('/api/identity/sessions/refresh', {
        method: 'POST',
        headers: { accept: 'application/json', ...CLIENT_HEADER },
      });
      if (!response.ok) {
        setToken(null);
        return false;
      }
      setToken(authenticatedResponseSchema.parse(await response.json()).accessToken);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function signOut(): Promise<void> {
  try {
    await fetch('/api/identity/sessions/logout', {
      method: 'POST',
      headers: { authorization: `Bearer ${accessToken ?? ''}`, ...CLIENT_HEADER },
    });
  } finally {
    setToken(null);
  }
}

export function acceptSession(session: AuthenticatedResponse): void {
  setToken(session.accessToken);
}

async function toError(response: Response): Promise<ApiError> {
  let raw: string | null = null;
  const fields: Record<string, string> = {};
  try {
    const body = (await response.json()) as {
      message?: unknown;
      field?: unknown;
      issues?: unknown;
    };
    if (typeof body.message === 'string') raw = body.message;
    else if (Array.isArray(body.message)) raw = body.message.join(' · ');
    if (typeof body.field === 'string' && raw !== null) fields[body.field] = friendlyMessage(raw);
    if (Array.isArray(body.issues)) {
      for (const issue of body.issues as { path?: unknown; message?: unknown }[]) {
        if (typeof issue.path === 'string' && typeof issue.message === 'string') {
          fields[issue.path] = friendlyMessage(issue.message);
        }
      }
    }
  } catch {
    // not JSON
  }
  if (raw !== null) return new ApiError(response.status, friendlyMessage(raw), fields);
  if (response.status === 401) return new ApiError(401, 'A sessão expirou. Entra de novo.');
  if (response.status === 403) return new ApiError(403, 'Sem permissão.');
  if (response.status === 429) {
    return new ApiError(429, 'Demasiadas tentativas. Espera um minuto e tenta de novo.');
  }
  return new ApiError(response.status, `Erro ${String(response.status)}. Tenta de novo.`);
}

function jsonHeaders(withBody: boolean): Record<string, string> {
  return {
    accept: 'application/json',
    ...(withBody ? { 'content-type': 'application/json' } : {}),
  };
}

/** Calls without a session (sign-up, sign-in steps, email links). */
export async function post<S extends z.ZodTypeAny>(
  path: string,
  body: unknown,
  schema: S,
): Promise<z.output<S>> {
  const response = await postRaw(path, body);
  return schema.parse(await response.json()) as z.output<S>;
}

/** Like `post`, for endpoints that answer 204. */
export async function postRaw(path: string, body: unknown): Promise<Response> {
  const response = await fetch(path, {
    method: 'POST',
    headers: { ...jsonHeaders(true), ...CLIENT_HEADER },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await toError(response);
  return response;
}

export async function request(method: string, path: string, body?: unknown): Promise<Response> {
  const attempt = () =>
    fetch(path, {
      method,
      headers: {
        ...jsonHeaders(body !== undefined),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  let response = await attempt();
  // An expired access token is normal every 15 minutes: renew once and retry.
  if (response.status === 401 && (await refreshSession())) response = await attempt();
  if (!response.ok) throw await toError(response);
  return response;
}

export async function getJson<S extends z.ZodTypeAny>(
  path: string,
  schema: S,
): Promise<z.output<S>> {
  const response = await request('GET', path);
  return schema.parse(await response.json()) as z.output<S>;
}

export async function send<S extends z.ZodTypeAny>(
  method: string,
  path: string,
  body: unknown,
  schema: S,
): Promise<z.output<S>>;
export async function send(method: string, path: string, body?: unknown): Promise<unknown>;
export async function send(
  method: string,
  path: string,
  body?: unknown,
  schema?: z.ZodTypeAny,
): Promise<unknown> {
  const response = await request(method, path, body);
  if (response.status === 204) return null;
  const text = await response.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  return schema ? schema.parse(json) : json;
}

export function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Erro desconhecido.';
}

export function fieldsOf(error: unknown): Record<string, string> {
  return error instanceof ApiError ? error.fields : {};
}

/**
 * Downloads a file that needs the session (the guardian's PDF): fetched with
 * the access token, then handed to the browser as a download.
 */
export async function download(path: string, fallbackName: string): Promise<void> {
  const response = await request('GET', path);
  const disposition = response.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Give the browser a moment to start the download before letting go.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}
